import { motion } from "framer-motion";
import profile from "@/data/profile";
import { stats as statsFallback } from "@/data/stats";
import StatsCounter from "@/components/site/StatsCounter";
import { useSiteContent } from "@/lib/SiteContentProvider";
import SectionGlow from "@/components/site/SectionGlow";

// Renders a single inline {gold:...} token within headline text. Plain text stays untouched.
const renderHeadline = (text) => {
    if (!text) return null;
    const parts = [];
    let remaining = text;
    let key = 0;
    const re = /\{gold:([^}]+)\}/g;
    let lastIndex = 0;
    let match;
    while ((match = re.exec(remaining)) !== null) {
        if (match.index > lastIndex) parts.push(<span key={key++}>{remaining.slice(lastIndex, match.index)}</span>);
        parts.push(<span key={key++} className="pg-text-gold-static">{match[1]}</span>);
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) parts.push(<span key={key++}>{remaining.slice(lastIndex)}</span>);
    return parts;
};

export const About = () => {
    const { data } = useSiteContent();
    const a = data?.about || {};
    const founderImage = a.image || profile.founderPhoto;
    const imagePosition = a.image_position || "50% 30%";
    const founderName = a.founder_name || "Pranto";
    const chipLabel = a.founder_chip_label || "Founder · Studio Lead";
    const roleChips = Array.isArray(a.founder_role_chips) && a.founder_role_chips.length
        ? a.founder_role_chips
        : ["Creative Director", "Brand Identity Expert", "Brand Systems Architect"];
    const headline = a.headline || "A decade of building brands that look {gold:unmistakably premium}.";
    const paragraphs = Array.isArray(a.paragraphs) && a.paragraphs.length ? a.paragraphs : [];
    const specialties = Array.isArray(a.specialties) && a.specialties.length
        ? a.specialties
        : ["Strategic Branding", "Visual Identity Systems", "UI/UX Direction", "Digital Brand Experiences", "High-Impact Creative Systems"];
    const statsForCounter = data?.stats?.items || statsFallback;

    return (
        <section id="about" className="pg-section" data-testid="about-section" aria-label="About Pranto">
            <SectionGlow tone="warm" />
            <div className="pg-container">
                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
                    {/* Founder photo card */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-5"
                    >
                        <div
                            className="relative aspect-[4/5] overflow-hidden rounded-3xl border"
                            style={{ borderColor: "var(--pg-border)", background: "var(--pg-surface)" }}
                        >
                            <img
                                src={founderImage}
                                alt={`${founderName}, Creative Director and Brand Systems Architect`}
                                data-testid="about-founder-photo"
                                className="h-full w-full object-cover"
                                style={{ objectPosition: imagePosition }}
                                loading="lazy"
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(11,59,58,0.05) 0%, rgba(11,59,58,0.0) 50%, rgba(7,17,18,0.55) 100%)",
                                }}
                            />
                            <div className="pg-noise" />

                            <div
                                className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 backdrop-blur"
                                style={{
                                    borderColor: "rgba(255,255,255,0.18)",
                                    background: "rgba(0,0,0,0.32)",
                                    color: "#FFFFFF",
                                }}
                            >
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: "var(--pg-accent)" }} />
                                <span className="font-mono text-[11px] uppercase tracking-[0.18em]">
                                    {chipLabel}
                                </span>
                            </div>

                            <div className="absolute bottom-5 left-5 right-5">
                                <div className="font-display text-3xl text-white">{founderName}</div>
                                <div className="mt-1 flex flex-wrap gap-2">
                                    {roleChips.slice(0, 3).map((p) => (
                                        <span
                                            key={p}
                                            className="font-mono text-[10px] uppercase tracking-[0.18em] rounded-full px-2.5 py-1 border"
                                            style={{
                                                borderColor: "rgba(255,255,255,0.22)",
                                                background: "rgba(0,0,0,0.32)",
                                                color: "#FFFFFF",
                                            }}
                                        >
                                            {p}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Narrative */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.7, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-7"
                    >
                        <h2
                            className="pg-h2-section"
                            style={{ color: "var(--pg-fg)" }}
                            data-testid="about-headline"
                        >
                            {renderHeadline(headline)}
                        </h2>

                        <div className="pg-lede mt-6 max-w-2xl space-y-5">
                            {paragraphs.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>

                        <div className="mt-8 flex flex-wrap gap-2" data-testid="about-specialties">
                            {specialties.map((s) => (
                                <span key={s} className="pg-chip">{s}</span>
                            ))}
                        </div>
                    </motion.div>
                </div>

                {/* Stats counters */}
                <div className="mt-14 sm:mt-20">
                    <StatsCounter items={statsForCounter} />
                </div>
            </div>
        </section>
    );
};

export default About;
